import { z } from 'zod';
import { DateTimePropSchema } from './dateTimePropEditor';

export const BuyXGetYPromoSchema = z.object({
    // Discount does not apply to taxes, other fees, and customizations.
    buyQ: z.number(),
    getQ: z.number(),
    maxFreeQ: z.number(),
    // % off base for each "get" unit. Missing = 100 (fully free). Does not stack with percentage_promo on those units.
    getDiscountPercent: z.number().min(0).max(100).optional(),
    dateTimeProp: DateTimePropSchema.optional(),
});
export type BuyXGetYPromo = z.infer<typeof BuyXGetYPromoSchema>;

export const BuyXGetYPromoCalculationResultSchema = BuyXGetYPromoSchema
    .omit({ dateTimeProp: true })
    .extend({
        discount: z.number(),
        freeItemsCount: z.number(),
    });
export type BuyXGetYPromoCalculationResultType = z.infer<typeof BuyXGetYPromoCalculationResultSchema>;

// Buy X Get Y free any of listed items (cheapest free). Discount does not apply to taxes, fees, or customizations
// except first-child option ids listed in getItemIds when the container item id is also in getItemIds (e.g. size fees marked free).
// Option ids alone do not make a cart line free-eligible; the top-level item id must be in getItemIds.
export const BuyXGetYAnyOfPromoSchema = z.object({
    buyQ: z.number(),
    getQ: z.number(),
    maxFreeQ: z.number(),
    // % off base for each "get" unit. Missing = 100 (fully free). Does not stack with percentage_promo on those units.
    getDiscountPercent: z.number().min(0).max(100).optional(),
    // Top-level menu item ids that may be free; first-customization option ids mark option fees free when container is listed
    getItemIds: z.array(z.string()).min(1),
    dateTimeProp: DateTimePropSchema.optional(),
});
export type BuyXGetYAnyOfPromo = z.infer<typeof BuyXGetYAnyOfPromoSchema>;

export const BuyXGetYAnyOfPromoCalculationResultSchema = BuyXGetYAnyOfPromoSchema
    .omit({ dateTimeProp: true })
    .extend({
        discount: z.number(),
        freeItemsCount: z.number(),
    });
export type BuyXGetYAnyOfPromoCalculationResultType = z.infer<typeof BuyXGetYAnyOfPromoCalculationResultSchema>;

export const PercentagePromoSchema = z.object({
    percentage: z.number().min(0).max(100),
    dateTimeProp: DateTimePropSchema.optional(),
});
export type PercentagePromo = z.infer<typeof PercentagePromoSchema>;

export const PercentagePromoCalculationResultSchema = PercentagePromoSchema
    .omit({ dateTimeProp: true })
    .extend({
        discount: z.number(),
    });
export type PercentagePromoCalculationResultType = z.infer<typeof PercentagePromoCalculationResultSchema>;


export const ItemSchema = z.object({
    id: z.string().optional(),
    lbl: z.string().optional(), // label, title, name
    dsc: z.string().optional(), // description
    prc: z.number().optional(), // price
    pickupPrc: z.number().optional(), // override pickup price

    cIds: z.array(z.string()).optional(), // children ids

    opPrc: z.number().optional(), // option price

    chrgAbvQ: z.number().optional(), // charge above quantity

    defQ: z.number().optional(), // pre selected/default quantity

    maxQ: z.number().optional(), // max quantity
    minQ: z.number().optional(), // min quantity

    // type
    // item, category, customization, option
    // item:i, category:ct, customization:c, option:o
    // i, ct, c, o
    t: z.enum(['i', 'ct', 'c', 'o']).optional(), // type

    // tax settings
    taxCatId: z.string().optional(), // tax category id

    photos: z.array(z.string()).max(10).optional(), // first photo = menu thumb

    // promo: buy 1 get 1 free
    // If both buyX_GetY and buyX_GetY_anyOf are active on the same item, anyOf wins.
    buyX_GetY_promo_delivery: BuyXGetYPromoSchema.optional(),
    buyX_GetY_promo_pickup: BuyXGetYPromoSchema.optional(),

    // promo: buy X get Y free any of listed items (cheapest)
    // Overrides buyX_GetY_promo_* when both are active (same order type).
    buyX_GetY_anyOf_promo_delivery: BuyXGetYAnyOfPromoSchema.optional(),
    buyX_GetY_anyOf_promo_pickup: BuyXGetYAnyOfPromoSchema.optional(),

    // promo: percentage discount
    // Applies only to paid units (not units already discounted by buyX_GetY / anyOf).
    percentage_promo_delivery: PercentagePromoSchema.optional(),
    percentage_promo_pickup: PercentagePromoSchema.optional(),
});

export const MenuRootSchema = z.object({
    children: z.array(z.string()).optional(),
    allItems: z.record(z.string(), ItemSchema).optional(),
    percentage_promo_min_subtotal: z.number().optional(), // when undefined, assume it is 0
});

export type Item = z.infer<typeof ItemSchema>;
export type MenuRoot = z.infer<typeof MenuRootSchema>;
export type ItemId = string;
export type ParentItemId = ItemId;
